// src/helpers/auth-finalize.js
//
// Purpose
// -------
// This module centralizes the logic that "finishes" an authenticated
// session after we have an access token. Before this file, the
// following steps were duplicated in multiple places:
//   1) Setting the active MSAL account
//   2) Calling the Identity API to retrieve the user profile
//   3) Persisting the access token and user details into storage
//   4) Dispatching Redux success and navigating to the correct page
//
// By moving this into a single helper, we:
//   - Reduce duplication and the chance of future drift
//   - Make the flow easier to understand for new developers
//   - Keep behaviour identical across Login, WaitingLogin, and redirect paths

import config from "config";
import LZString from "lz-string";

import { alertActions } from "../actions/alert.actions";
import { SILENT_REDIRECT_URI } from "../authConfig";
import { userConstants } from "../constants";
import msalInstance from "../msalInstance";
import { store } from "../store";
import { clearAuthRedirectPending } from "./authRedirectPending";
import client from "./client";
import { setGlobalClientId } from "./client";
import { getIsDeepLinkE2eSession } from "./cypressE2eFlags";
import { getIsCypressAut } from "./isCypressAut";
import {
  armPostAuthGrace,
  clearDeepLinkHistoryPair,
  isDeepLinkHistoryStaleForMsalAccount,
} from "./postAuthGrace";

const { API } = config;

function sleep(ms) {
  return new Promise((resolve) => setTimeout(resolve, ms));
}

function isRetryableError(error) {
  try {
    if (!error) return true;
    // Network errors or timeouts
    if (!error.response) return true;
    const status = error.response.status;
    // Retry on 5xx and 429
    return status >= 500 || status === 429;
  } catch (_e) {
    return true;
  }
}

export const buildAcquireRequest = (account, lastLoginType) => {
  // Build a token acquisition request that uses the correct authority
  // for the current login type (email vs SSO) and always requests our
  // standard API scopes. We keep forceRefresh=false here because the
  // token we just acquired from MSAL redirect or silent flows is fresh.
  return {
    scopes: ["openid", ...config.adsMsalConfig.b2cScopes],
    account,
    authority:
      lastLoginType === "email"
        ? config.emailMsalConfig?.authority ||
          config.adsMsalConfig.authority
        : config.adsMsalConfig.authority,
    forceRefresh: false,
    redirectUri: SILENT_REDIRECT_URI,
  };
};

async function fetchUserWithAccessToken(accessToken) {
  // Helper to read the currently authenticated user for this token.
  // We only need a subset of the user data here, but we keep the call
  // identical to the historical flow for full compatibility.
  const response = await client.get(
    `${API}/api/Identity/noCompanyContracts`,
    {
      headers: { Authorization: `Bearer ${accessToken}` },
    },
  );
  return response?.data?.user;
}

async function fetchUserWithAccessTokenWithRetry(accessToken) {
  // Added: 4 attempts with backoff; treat incomplete payloads as retryable
  const MAX_ATTEMPTS = 4;
  let lastError;
  for (let attempt = 1; attempt <= MAX_ATTEMPTS; attempt++) {
    try {
      const user = await fetchUserWithAccessToken(accessToken);
      // Require at least an id; occasionally backend may temporarily return a partial payload
      if (user && user.id) {
        return user;
      }
      // Incomplete payload, trigger retry
      lastError = new Error("Incomplete user profile");
    } catch (err) {
      lastError = err;
      if (!isRetryableError(err)) {
        break;
      }
    }
    // Exponential backoff: 200ms, 500ms, 1000ms, 2000ms
    let delay;
    if (attempt === 1) {
      delay = 200;
    } else if (attempt === 2) {
      delay = 500;
    } else if (attempt === 3) {
      delay = 1000;
    } else {
      delay = 2000;
    }
    await sleep(delay);
  }
  throw lastError || new Error("Unable to fetch user profile");
}

function isOnExchangesOrPbhRouteHash() {
  if (typeof window === "undefined") return false;
  const h = window.location.hash || "";
  return (
    /\/exchanges/i.test(h) || /pbhnew/i.test(h) || /\/orders\/pbh/i.test(h)
  );
}

export function isOnUsersRouteHash() {
  if (typeof window === "undefined") return false;
  return /\/users/i.test(window.location.hash || "");
}

function computePostAuthFallbackRoute(user) {
  const eligible =
    user.userPermissions &&
    user.userPermissions.length > 0 &&
    user.teamUsers &&
    user.teamUsers.length > 0;
  return eligible ? "#/dashboard" : "#/holding";
}

function redirectWithGrace(target, fallback) {
  armPostAuthGrace(target, fallback);
  window.location.href = target;
}

export async function finalizeAuthenticatedSession(account, accessToken) {
  // Centralized finalize flow (replaces bespoke handleLogin path):
  // 1) Ensure MSAL has the correct active account selected
  // 2) Fetch the user profile from the backend using the access token
  // 3) Persist token and user into storage (legacy-compatible keys)
  // 4) Dispatch Redux success and navigate to the correct landing page
  try {
    if (account) {
      try {
        msalInstance.setActiveAccount(account);
      } catch (_e) {
        // ignore
      }
    }

    const user = await fetchUserWithAccessTokenWithRetry(accessToken);
    if (!user) {
      throw new Error("User profile not available");
    }

    clearAuthRedirectPending();

    setGlobalClientId(user);

    try {
      // Persist fresh token so subsequent API calls carry the right bearer
      localStorage.setItem(
        `ads.auth.token.key${config.adsMsalConfig.clientID}`,
        JSON.stringify(accessToken),
      );

      // Persist the user (compressed, per historical format)
      const compressed = LZString.compressToUTF16(JSON.stringify(user));
      localStorage.setItem(
        `ads.user.id.key${config.adsMsalConfig.clientID}`,
        compressed,
      );

      // Store compact login hint (email) instead of full account JSON
      if (user.email) {
        localStorage.setItem("ads.loginHint", user.email);
      }
      // Clear last login type hint once we've finalized a session
      try {
        localStorage.removeItem("ads.auth.lastLoginType");
      } catch (_e) {
        // ignore storage errors
      }
    } catch (_e) {
      // ignore storage quota errors
    }

    store.dispatch({
      type: userConstants.MSAL_LOGIN_SUCCESS,
      msalUser: user,
    });

    // E2E drives the hash (e.g. /exchanges, /users). Stale sessionStorage "history"
    // from a prior tab/session is not overwritten when the URL has no query (? and =),
    // so finalize must not redirect or tests jump to random routes (e.g. mystock).
    if (
      getIsCypressAut() ||
      getIsDeepLinkE2eSession() ||
      isOnUsersRouteHash()
    ) {
      clearDeepLinkHistoryPair();
      return;
    }

    const fallbackRoute = computePostAuthFallbackRoute(user);
    const msalAccount = account || msalInstance.getActiveAccount();

    // MSAL can call finalize after the shell is already on Exchanges/PBH/Users (e.g. a
    // fast redirect). Do not overwrite with sessionStorage "history" or dashboard.
    if (isOnExchangesOrPbhRouteHash() || isOnUsersRouteHash()) {
      clearDeepLinkHistoryPair();
      const curHash =
        typeof window !== "undefined" ? window.location.hash || "" : "";
      armPostAuthGrace(curHash || fallbackRoute, fallbackRoute);
      return;
    }

    // Route using existing app rules (dashboard vs holding)
    // - If user has permissions and team setup: dashboard (or saved deep link)
    // - Otherwise: holding page (as historically implemented)
    if (
      user.userPermissions &&
      user.userPermissions.length > 0 &&
      user.teamUsers &&
      user.teamUsers.length > 0
    ) {
      if (isDeepLinkHistoryStaleForMsalAccount(msalAccount)) {
        clearDeepLinkHistoryPair();
      }
      const historyHash = sessionStorage.getItem("history");
      if (historyHash) {
        // Router may have reached /exchanges (or PBH) while Identity was in flight; do
        // not clobber that with a stale saved hash (common E2E flake).
        if (
          getIsCypressAut() ||
          getIsDeepLinkE2eSession() ||
          isOnExchangesOrPbhRouteHash() ||
          isOnUsersRouteHash()
        ) {
          clearDeepLinkHistoryPair();
          return;
        }
        redirectWithGrace(historyHash, fallbackRoute);
        clearDeepLinkHistoryPair();
        return;
      }
      redirectWithGrace("#/dashboard", fallbackRoute);
    } else {
      redirectWithGrace("#/holding", "#/holding");
    }
  } catch (error) {
    clearAuthRedirectPending();
    // Preserve historical error handling and surface a useful message
    store.dispatch({ type: userConstants.MSAL_LOGIN_FAILURE, error });
    store.dispatch(alertActions.error(String(error)));
    throw error;
  }
}
